import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../core/authContext';
import { fetchDistrictExitCriteria, type DistrictExitCriteria } from '../../core/exitCriteriaApi';
import { FEATURE_LANGUAGE_OVER_TIME } from '../../core/features';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { fetchFlashlightAssignments, fetchFlashlightBenchmarks, type FlashlightAssignmentsResponse, type FlashlightBenchmarksResponse } from '../IndividualPlan/flashlightBenchmarks';
import { fetchPlanRecord, type PlanRecord } from '../IndividualPlan/planRecordApi';
import { planPath } from '../IndividualPlan/planRoute';
import { criteriaForGrade, gradeKey } from '../IndividualPlan/reclassCriteriaContext';
import { TopBar } from '../IndividualPlan/TopBar';
import { fetchDataStatus, type PeDataStatus } from '../ProgramEvaluation/dataStatus';
import { AccessCard, type AccessUploaded, type ExitBar, type ExitCriteriaState } from './profile/AccessCard';
import { ElPlansCard } from './profile/ElPlansCard';
import { ElStatusCard } from './profile/ElStatusCard';
import { LanguageOverTime } from './profile/LanguageOverTime';
import { CARD_TEXT, ProfileCard } from './profile/ProfileCard';
import { ProfileHeader } from './profile/ProfileHeader';
import { fetchStudentProfile, StudentProfileFetchError, type StudentProfile } from './studentProfileApi';
import { ROSTER } from './tokens';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type Loaded = { kind: 'ready'; profile: StudentProfile } | { kind: 'missing' } | { kind: 'failed' };

const OUTAGE = 'We could not load your data. Nothing has been lost. Refresh the page to try again.';

type CriteriaRead = { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; criteria: DistrictExitCriteria };

function exitBars(read: CriteriaRead, profile: StudentProfile): ExitCriteriaState {
  if (read.kind !== 'ready') return read.kind;
  const grade = gradeKey(profile.access.administrations[0]?.grade) ?? gradeKey(profile.student.grade);
  const plan = criteriaForGrade(read.criteria, grade);
  const review = plan.review ? { bars: barsOf(plan.review.conditions), ...(plan.review.displayNote ? { note: plan.review.displayNote } : {}) } : undefined;
  return { state: plan.state, outcome: plan.outcome ?? 'exits', ...(plan.displayNote ? { note: plan.displayNote } : {}), bars: barsOf(plan.conditions ?? []), noExit: plan.overallLabel === 'Cannot exit on ACCESS', ...(review ? { review } : {}) };
}

function barsOf(conditions: { measure: string; minimum: number }[]): ExitBar[] {
  const bars: ExitBar[] = [];
  for (const condition of conditions) {
    const key = condition.measure.trim().toLowerCase();
    if (!key || bars.some((bar) => bar.key === key)) continue;
    bars.push({ key, label: key === 'overall' ? 'Overall' : key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '), minimum: condition.minimum });
  }
  return bars;
}


function Notice({ title, text, onBack }: { title: string; text?: string; onBack: () => void }) {
  return (
    <Box sx={{ p: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
      <Typography sx={{ fontFamily: ROSTER.font, fontSize: 20, fontWeight: 700, lineHeight: '28px', color: ROSTER.ink }}>{title}</Typography>
      {text && <Typography sx={{ fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.muted }}>{text}</Typography>}
      <Button onClick={onBack} sx={{ textTransform: 'none', fontFamily: ROSTER.font, fontWeight: 600, ml: '-8px' }}>
        Back to Students
      </Button>
    </Box>
  );
}

function cardGridSx(count: number) {
  const columns = (n: number) => `repeat(${n}, minmax(0, 1fr))`;
  if (count === 4) {
    return { gridTemplateColumns: columns(1), '@container (min-width: 584px)': { gridTemplateColumns: columns(2) }, '@container (min-width: 1192px)': { gridTemplateColumns: columns(4) } };
  }
  return { gridTemplateColumns: columns(2), '& > :last-child': { gridColumn: '1 / -1' }, '@container (min-width: 768px)': { gridTemplateColumns: columns(3), '& > :last-child': { gridColumn: 'auto' } } };
}

function ProgressReportCard({ onOpen }: { onOpen: () => void }) {
  return (
    <ProfileCard title="Student Progress Report">
      <Typography sx={{ ...CARD_TEXT.lead, color: ROSTER.muted }}>Flashlight 360 benchmarks and assignments, by window.</Typography>
      <Box sx={{ display: 'flex', mt: 'auto' }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={onOpen}
          sx={{ textTransform: 'none', fontFamily: ROSTER.font, fontWeight: 600, borderColor: ROSTER.border, '&:hover': { borderColor: ROSTER.border, bgcolor: ROSTER.hoverBg } }}
        >
          View progress report
        </Button>
      </Box>
    </ProfileCard>
  );
}

function useRead<T>(key: string | null, read: () => Promise<T>): { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; value: T } {
  const [state, setState] = useState<{ key: string; result: { kind: 'failed' } | { kind: 'ready'; value: T } } | null>(null);
  useEffect(() => {
    if (key == null) return undefined;
    let active = true;
    read()
      .then((value) => {
        if (active) setState({ key, result: { kind: 'ready', value } });
      })
      .catch(() => {
        if (active) setState({ key, result: { kind: 'failed' } });
      });
    return () => {
      active = false;
    };
    // `read` is rebuilt every render; the key names what it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state?.key === key ? state.result : { kind: 'loading' };
}

export function StudentProfilePage({
  load = fetchStudentProfile,
  dataStatusSource = fetchDataStatus,
  criteriaSource = fetchDistrictExitCriteria,
  planRecordSource = fetchPlanRecord,
  benchmarksSource = fetchFlashlightBenchmarks,
  assignmentsSource = fetchFlashlightAssignments,
}: {
  load?: (studentId: string) => Promise<StudentProfile>;
  dataStatusSource?: () => Promise<PeDataStatus>;
  criteriaSource?: () => Promise<DistrictExitCriteria>;
  planRecordSource?: (studentId: string, schoolYear: string) => Promise<PlanRecord>;
  benchmarksSource?: (studentId: string) => Promise<FlashlightBenchmarksResponse>;
  assignmentsSource?: (studentId: string) => Promise<FlashlightAssignmentsResponse>;
} = {}) {
  const navigate = useNavigate();
  const { studentId = '' } = useParams();
  const { hasFeature } = useAuth();
  const languageOverTime = hasFeature(FEATURE_LANGUAGE_OVER_TIME);
  const [state, setState] = useState<{ studentId: string; result: Loaded } | null>(null);
  const [accessUploaded, setAccessUploaded] = useState<AccessUploaded>('loading');
  const [criteria, setCriteria] = useState<CriteriaRead>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    load(studentId)
      .then((profile) => {
        if (active) setState({ studentId, result: { kind: 'ready', profile } });
      })
      .catch((err: unknown) => {
        const missing = err instanceof StudentProfileFetchError && (err.status === 404 || err.status === 403);
        if (active) setState({ studentId, result: { kind: missing ? 'missing' : 'failed' } });
      });
    criteriaSource()
      .then((next) => {
        if (active) setCriteria({ kind: 'ready', criteria: next });
      })
      .catch(() => {
        if (active) setCriteria({ kind: 'failed' });
      });
    return () => {
      active = false;
    };
  }, [studentId, load, criteriaSource]);

  const loaded = state?.studentId === studentId ? state.result : null;
  const role = loaded?.kind === 'ready' ? loaded.profile.role : null;
  useEffect(() => {
    if (role !== 'admin') return undefined;
    let active = true;
    dataStatusSource()
      .then((status) => {
        if (active) setAccessUploaded(status.accessPresent);
      })
      .catch(() => {
        if (active) setAccessUploaded(null);
      });
    return () => {
      active = false;
    };
  }, [role, dataStatusSource]);

  const profile = loaded?.kind === 'ready' ? loaded.profile : null;
  const benchmarks = useRead(languageOverTime && profile ? profile.student.id : null, () => benchmarksSource(profile!.student.id));
  const assignments = useRead(languageOverTime && profile ? profile.student.id : null, () => assignmentsSource(profile!.student.id));
  const showProgressCard = !languageOverTime && profile != null && profile.standing !== 'departed';
  const name = profile ? `${profile.student.firstName ?? ''} ${profile.student.lastName ?? ''}`.trim() || `Student ${profile.student.id}` : '';
  const openPlan = (id: string, schoolYear?: string) => navigate(planPath(id, schoolYear));
  const bars = profile ? exitBars(criteria, profile) : null;
  const openReport = () => navigate(`/reports/student-progress/${encodeURIComponent(profile!.student.id)}`, { state: { from: 'profile' } });
  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: ROSTER.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="roster" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar crumbs={[{ label: 'Students', to: '/roster' }, ...(name ? [{ label: name }] : [])]} />
        {loaded?.kind === 'missing' && <Notice title="We can’t show this student." text="They may not be on your roster." onBack={() => navigate('/roster')} />}
        {loaded?.kind === 'failed' && <Notice title={OUTAGE} onBack={() => navigate('/roster')} />}
        {profile && (
          <Box sx={{ p: '32px 24px 72px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <ProfileHeader profile={profile} />
            <Box sx={{ containerType: 'inline-size' }}>
              <Box data-testid="profile-cards" data-cards={showProgressCard ? 4 : 3} sx={{ display: 'grid', gap: '24px', alignItems: 'stretch', ...cardGridSx(showProgressCard ? 4 : 3) }}>
                <ElStatusCard profile={profile} />
                <AccessCard profile={profile} criteria={bars} accessUploaded={profile.role === 'admin' ? accessUploaded : null} />
                <ElPlansCard profile={profile} onOpenPlan={openPlan} recordSource={planRecordSource} />
                {showProgressCard && <ProgressReportCard onOpen={openReport} />}
              </Box>
            </Box>
            {languageOverTime && (
              <LanguageOverTime
                profile={profile}
                bars={bars && typeof bars === 'object' ? bars.bars : null}
                stateName={criteria.kind === 'ready' ? criteria.criteria.stateName : undefined}
                outcome={bars && typeof bars === 'object' ? bars.outcome : 'exits'}
                benchmarks={benchmarks}
                assignments={assignments}
                onOpenReport={openReport}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
